/**
 * Mallas procedurales. Ni un asset descargado: cero copyright y cero bytes de
 * imagenes o modelos en el bundle.
 *
 * Regla de rendimiento de este fichero: las geometrias y los materiales se
 * crean UNA vez y se reutilizan. En un Android de gama media, crear geometrias
 * mientras el juego corre es un tiron garantizado, y el presupuesto son 60 fps.
 * Por eso todo lo de abajo son funciones `crearX()` que se llaman al arrancar,
 * y los objetos de la pista se clonan de esas plantillas.
 */

import {
  AdditiveBlending,
  CanvasTexture,
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  CapsuleGeometry,
  ConeGeometry,
  CylinderGeometry,
  Group,
  IcosahedronGeometry,
  Mesh,
  MeshLambertMaterial,
  MeshBasicMaterial,
  OctahedronGeometry,
  PlaneGeometry,
  Points,
  PointsMaterial,
  RepeatWrapping,
  SphereGeometry,
  TorusGeometry,
  type Texture,
} from 'three';
import { brillo } from './cielo';
import { COLORES } from './paleta';

/**
 * Materiales. MeshLambertMaterial y no Standard/Physical a proposito: Lambert
 * no calcula reflejos ni rugosidad, y en un movil de gama media eso son varios
 * fps de diferencia que aqui no compran nada, porque el estilo es low-poly
 * plano. Se comparten entre todas las piezas del mismo tipo.
 */
function mat(color: number): MeshLambertMaterial {
  return new MeshLambertMaterial({ color });
}

/**
 * Material de neon: **no se ilumina, se dibuja ya brillante.**
 *
 * Es la pieza clave de como se ve el juego desde el 1-oct-2026. Un filo pintado
 * con un material iluminado depende de donde caiga la luz, asi que a veces se
 * ve y a veces no; uno sin luz sale siempre a todo color y se recorta contra el
 * fondo oscuro pase lo que pase.
 *
 * Y ademas es **mas barato**: `MeshBasicMaterial` no hace ni un calculo de
 * iluminacion. Aqui lo bonito y lo rapido van en la misma direccion, que no
 * suele pasar.
 */
function neon(color: number): MeshBasicMaterial {
  return new MeshBasicMaterial({ color, fog: true });
}

/**
 * Un halo: un plano con degradado radial y mezcla aditiva.
 *
 * Hace el papel del resplandor sin pagar un pase de post-proceso. El bloom de
 * verdad (EffectComposer + UnrealBloomPass) son ~25 KB de bundle y una pasada
 * a pantalla completa por cuadro; en un Android de gama media eso se nota, y
 * aqui el efecto que se busca es un halo alrededor de cuatro cosas concretas,
 * no un resplandor general.
 *
 * `depthWrite: false` para que los halos no se tapen entre si, y `sizeAttenuation`
 * implicito: al ser geometria, se encoge con la distancia solo.
 */
function halo(color: number, tamano: number, opacidad = 0.55): Mesh {
  const m = new Mesh(
    new PlaneGeometry(tamano, tamano),
    new MeshBasicMaterial({
      map: brillo(),
      color,
      transparent: true,
      opacity: opacidad,
      blending: AdditiveBlending,
      depthWrite: false,
      fog: false,
    })
  );
  m.renderOrder = 2;
  return m;
}

export interface Plantillas {
  jugador: Group;
  barrera: Group;
  vela: Group;
  tiburon: Group;
  moneda: Group;
  iman: Group;
  x2: Group;
  escudo: Group;
  cohete: Group;
  ballena: Group;
}

// ---------------------------------------------------------------------------
// Jugador
// ---------------------------------------------------------------------------

/**
 * El personaje: un piloto sobre una tabla propulsada. Mira hacia -Z.
 *
 * ---------------------------------------------------------------------------
 * POR QUE ES ASI (1-oct-2026)
 * ---------------------------------------------------------------------------
 * El primero era una capsula naranja con una bola encima, y de espaldas —que es
 * como se le ve el 100% del tiempo— se leia como una mancha. El dueno lo miro
 * como jugador y dijo que no era bonito, y tenia razon.
 *
 * Lo que cambia, y el porque de cada cosa:
 *
 * **1. Traje oscuro con filos de neon.** Antes el cuerpo era naranja entero, y
 * una figura clara sobre un fondo claro no tiene silueta. Ahora el cuerpo es
 * casi negro y el naranja va solo en los bordes: la figura se recorta contra
 * cualquier fondo, que es la definicion de una buena silueta.
 *
 * **2. La tabla.** Da una base ancha y horizontal que el ojo reconoce de
 * inmediato, y resuelve un problema viejo: una figura humana corriendo, vista
 * de espaldas y sin piernas animadas, se ve rara. Sobre una tabla no corre:
 * vuela, y entonces no hace falta animar nada.
 *
 * **3. Los motores son lo mas brillante de la pantalla.** Dos toberas de cian
 * con su halo, encendidas siempre. Ademas de ser el ancla visual, cuentan de
 * que va el juego sin una sola palabra: esto es un cohete, y va hacia arriba.
 *
 * **4. Nada que no se vea.** La cara, el visor y el detalle frontal estan
 * fuera: la camara va detras y nunca se ven. Cada triangulo que no se ve es un
 * triangulo que no se procesa sesenta veces por segundo.
 */
function crearJugador(): Group {
  const g = new Group();

  // --- La tabla ---
  //
  // Va en su propio grupo con nombre para poder inclinarla sola: al cambiar de
  // carril la tabla gira MAS que el cuerpo, igual que en una tabla de verdad,
  // y esa diferencia entre las dos es lo que hace que el giro se sienta.
  const grupoTabla = new Group();
  grupoTabla.name = 'tabla';
  g.add(grupoTabla);

  const tabla = new Mesh(new BoxGeometry(0.92, 0.09, 1.6), mat(0x12152e));
  tabla.position.y = 0.12;
  grupoTabla.add(tabla);

  // El filo de neon del canto. Dos tiras finas, una por lado.
  for (const x of [-0.47, 0.47]) {
    const canto = new Mesh(new BoxGeometry(0.05, 0.05, 1.5), neon(COLORES.jugadorFilo));
    canto.position.set(x, 0.12, 0);
    grupoTabla.add(canto);
  }

  // El resplandor de debajo de la tabla: lo que la hace parecer que flota.
  const bajoTabla = halo(COLORES.jugadorFilo, 2.1, 0.5);
  bajoTabla.rotation.x = -Math.PI / 2;
  bajoTabla.position.y = 0.03;
  grupoTabla.add(bajoTabla);

  // --- El cuerpo ---
  // Inclinado hacia delante: una figura vertical se ve parada, una inclinada se
  // ve lanzada. Es el mismo numero de triangulos y cambia la sensacion entera.
  const torso = new Mesh(new CapsuleGeometry(0.3, 0.44, 4, 8), mat(COLORES.jugador));
  torso.position.set(0, 0.76, 0.02);
  torso.rotation.x = -0.14;
  g.add(torso);

  // Las dos tiras de neon del traje, por los COSTADOS.
  //
  // Estaban en el pecho (z negativa) y no se veian: la camara va detras del
  // personaje, asi que todo lo que mira hacia delante es trabajo tirado. Sin
  // ellas el cuerpo desaparecia en negro y solo quedaban el arco del casco y
  // los dos motores, que juntos se leian como una cara.
  //
  // En los costados dibujan el contorno del torso, que es lo que hace que se
  // lea una persona.
  for (const x of [-0.28, 0.28]) {
    const tira = new Mesh(new BoxGeometry(0.05, 0.46, 0.14), neon(COLORES.jugadorFilo));
    tira.position.set(x, 0.78, 0.02);
    tira.rotation.x = -0.14;
    g.add(tira);
  }

  // La barra de los hombros: ensancha la silueta justo donde hace falta. Una
  // figura sin hombros, vista de espaldas, es un palo.
  const hombros = new Mesh(new BoxGeometry(0.66, 0.07, 0.12), neon(COLORES.jugadorFilo));
  hombros.position.set(0, 1.0, 0.06);
  g.add(hombros);

  // --- El casco ---
  //
  // En su propio grupo: la cabeza MIRA hacia donde va el jugador cuando cambia
  // de carril. Es el gesto que mas dice de los cuatro, porque mirar hacia donde
  // vas es lo primero que hace una persona al girar.
  const cabeza = new Group();
  cabeza.name = 'cabeza';
  cabeza.position.set(0, 1.27, 0.04);
  g.add(cabeza);

  const casco = new Mesh(new SphereGeometry(0.27, 10, 8), mat(COLORES.jugadorCasco));
  cabeza.add(casco);

  // La franja luminosa de la nuca: el unico detalle de la cabeza que se ve
  // desde atras, y lo que hace que la cabeza se lea como una cabeza.
  const nuca = new Mesh(new TorusGeometry(0.2, 0.035, 5, 10, Math.PI), neon(COLORES.jugadorVisor));
  nuca.position.set(0, 0.03, 0.12);
  nuca.rotation.set(Math.PI / 2, 0, 0);
  cabeza.add(nuca);

  // --- La mochila y los motores ---
  const mochila = new Mesh(new BoxGeometry(0.44, 0.46, 0.2), mat(0x1a2042));
  mochila.position.set(0, 0.88, 0.3);
  g.add(mochila);

  for (const x of [-0.15, 0.15]) {
    const tobera = new Mesh(new CylinderGeometry(0.075, 0.1, 0.18, 6), mat(0x2a3156));
    tobera.position.set(x, 0.62, 0.36);
    g.add(tobera);

    // El nucleo de la llama, sin luz: siempre a tope de color.
    const llama = new Mesh(new SphereGeometry(0.1, 7, 6), neon(COLORES.jugadorMotor));
    llama.position.set(x, 0.57, 0.46);
    llama.scale.set(1, 1, 1.5);
    g.add(llama);

    // Y su halo. Es lo que se ve de lejos y lo que convierte dos puntos en
    // "motores encendidos".
    const h = halo(COLORES.jugadorMotor, 1.0, 0.75);
    h.position.set(x, 0.57, 0.5);
    g.add(h);
  }

  // --- Los brazos ---
  //
  // Cada uno en un grupo con el pivote EN EL HOMBRO, no en el centro del brazo.
  // Es lo que permite que giren como giran los brazos: desde arriba. Si el
  // pivote estuviera en el medio, al rotarlos se separarian del cuerpo.
  for (const [nombre, x] of [
    ['brazoIzq', -0.37],
    ['brazoDer', 0.37],
  ] as const) {
    const hombro = new Group();
    hombro.name = nombre;
    hombro.position.set(x, 1.0, 0.06);
    // La postura de reposo: hacia atras, como quien va agarrado al viento.
    hombro.rotation.set(0.5, 0, x > 0 ? -0.32 : 0.32);
    g.add(hombro);

    const brazo = new Mesh(new CapsuleGeometry(0.085, 0.34, 3, 6), mat(COLORES.jugador));
    // Colgando del hombro: el centro de la capsula, medio brazo mas abajo.
    brazo.position.y = -0.21;
    hombro.add(brazo);

    // Un filo de neon tambien en el brazo, para que la silueta de los brazos se
    // vea contra el fondo igual que la del cuerpo.
    const filo = new Mesh(new BoxGeometry(0.04, 0.3, 0.04), neon(COLORES.jugadorFilo));
    filo.position.set(x > 0 ? 0.08 : -0.08, -0.2, 0);
    hombro.add(filo);
  }

  return g;
}

// ---------------------------------------------------------------------------
// Obstaculos
// ---------------------------------------------------------------------------

/**
 * Barrera: se SALTA. Baja y ancha, para que se lea como "por encima".
 *
 * Las medidas NO se tocan: 1,8 de ancho y 0,7 de alto son las que usa la
 * deteccion de choques (`medioAnchoChoque` 0,86 y `alturaParaLibrarBarrera`
 * 0,72 en `shared/nivel-config.json`). Si lo que se ve fuera mas pequeno que lo
 * que choca, el jugador moriria sin tocar nada y se iria del juego con razon.
 */
function crearBarrera(): Group {
  const g = new Group();

  const cuerpo = new Mesh(new BoxGeometry(1.8, 0.7, 0.3), mat(COLORES.barrera));
  cuerpo.position.y = 0.35;
  g.add(cuerpo);

  // El filo de arriba, en rojo encendido. **Esto es lo que hay que ver**: marca
  // exactamente la altura que hay que superar, y al ir sin luz se ve igual de
  // bien en la ciudad que en el espacio.
  const filo = new Mesh(new BoxGeometry(1.86, 0.1, 0.36), neon(COLORES.barreraFilo));
  filo.position.y = 0.68;
  g.add(filo);

  // Franjas de obra: el lenguaje universal de "no pases".
  for (const x of [-0.58, 0, 0.58]) {
    const franja = new Mesh(new BoxGeometry(0.3, 0.52, 0.33), neon(COLORES.barreraDetalle));
    franja.position.set(x, 0.3, 0);
    g.add(franja);
  }

  for (const x of [-0.85, 0.85]) {
    const pata = new Mesh(new BoxGeometry(0.12, 0.35, 0.12), mat(0x1c2038));
    pata.position.set(x, 0.17, 0);
    g.add(pata);
  }

  // Halo tumbado a sus pies: ancla la barrera al suelo y avisa de lejos.
  const h = halo(COLORES.barreraFilo, 2.6, 0.32);
  h.rotation.x = -Math.PI / 2;
  h.position.y = 0.04;
  g.add(h);

  return g;
}

/**
 * Vela roja (bear): se pasa por DEBAJO deslizandose. Es una vela de grafico de
 * velas, flotando a la altura del pecho, con su mecha arriba y abajo.
 * Flota a proposito: un obstaculo que no toca el suelo se lee como "agachate".
 */
function crearVela(): Group {
  const g = new Group();

  const cuerpo = new Mesh(new BoxGeometry(1.5, 1.1, 0.42), mat(COLORES.vela));
  cuerpo.position.y = 1.55;
  g.add(cuerpo);

  // El filo de ABAJO es el que importa: marca por donde hay que pasar
  // deslizandose. Antes la vela era un bloque rojo liso y no se sabia a que
  // altura empezaba, que es justo el dato que el jugador necesita.
  const filoBajo = new Mesh(new BoxGeometry(1.56, 0.09, 0.48), neon(COLORES.velaFilo));
  filoBajo.position.y = 1.02;
  g.add(filoBajo);

  const filoAlto = new Mesh(new BoxGeometry(1.56, 0.07, 0.48), neon(COLORES.velaFilo));
  filoAlto.position.y = 2.08;
  g.add(filoAlto);

  // La mecha de la vela de grafico, arriba y abajo.
  const mecha = new Mesh(new BoxGeometry(0.07, 2.5, 0.07), neon(COLORES.velaMecha));
  mecha.position.y = 1.55;
  g.add(mecha);

  // Halo vertical detras: dibuja la silueta del bloque contra el fondo.
  const h = halo(COLORES.velaFilo, 2.8, 0.3);
  h.position.set(0, 1.55, 0.3);
  g.add(h);

  return g;
}

/**
 * Tiburon FUD: no se salta ni se esquiva por debajo, hay que CAMBIARSE de
 * carril. Ocupa el carril de arriba abajo, y por eso la generacion del nivel
 * nunca cierra los tres carriles en la misma ranura.
 */
function crearTiburon(): Group {
  const g = new Group();

  const cuerpo = new Mesh(new ConeGeometry(0.62, 2.3, 8), mat(COLORES.tiburon));
  cuerpo.rotation.x = -Math.PI / 2;
  cuerpo.position.y = 1.1;
  g.add(cuerpo);

  const vientre = new Mesh(new ConeGeometry(0.4, 1.9, 7), mat(COLORES.tiburonVientre));
  vientre.rotation.x = -Math.PI / 2;
  vientre.position.set(0, 0.86, 0.1);
  g.add(vientre);

  // Aleta dorsal: la silueta que se reconoce a cien metros.
  const aleta = new Mesh(new ConeGeometry(0.3, 0.75, 4), mat(COLORES.tiburon));
  aleta.position.set(0, 1.85, 0.3);
  aleta.rotation.x = -0.25;
  g.add(aleta);

  for (const x of [-0.55, 0.55]) {
    const lateral = new Mesh(new ConeGeometry(0.2, 0.6, 4), mat(COLORES.tiburon));
    lateral.position.set(x, 0.85, 0.25);
    lateral.rotation.z = x > 0 ? -1.2 : 1.2;
    g.add(lateral);
  }

  // Dientes: tres conos pequenos. Mas que esto no se ve y cuesta vertices.
  for (const x of [-0.18, 0, 0.18]) {
    const diente = new Mesh(new ConeGeometry(0.07, 0.2, 3), neon(COLORES.tiburonDiente));
    diente.position.set(x, 1.0, -0.95);
    diente.rotation.x = Math.PI / 2;
    g.add(diente);
  }

  // Los ojos, encendidos. Dos puntos rojos a la altura de la cara convierten
  // una forma gris en algo que te mira, y eso se detecta mucho antes que una
  // silueta: el ojo humano busca ojos.
  for (const x of [-0.26, 0.26]) {
    const ojo = new Mesh(new SphereGeometry(0.075, 6, 5), neon(COLORES.tiburonOjo));
    ojo.position.set(x, 1.2, -0.52);
    g.add(ojo);
  }

  // La linea de la aleta, encendida: es la silueta que se reconoce a cien
  // metros, asi que merece ser lo que brilla.
  const bordeAleta = new Mesh(new BoxGeometry(0.06, 0.62, 0.06), neon(COLORES.tiburonFilo));
  bordeAleta.position.set(0, 1.95, 0.26);
  bordeAleta.rotation.x = -0.25;
  g.add(bordeAleta);

  // Halo rojo alrededor: este es el obstaculo que ocupa el carril entero y
  // obliga a cambiarse, asi que es el que mas pronto hay que ver venir.
  const h = halo(COLORES.tiburonFilo, 3.2, 0.26);
  h.position.set(0, 1.2, 0.5);
  g.add(h);

  return g;
}

// ---------------------------------------------------------------------------
// Recolectables
// ---------------------------------------------------------------------------

/**
 * La moneda. Es lo que mas veces se ve en toda la partida, asi que es lo que
 * mas tiene que brillar: va sin luz y con halo, para que se vea llegar desde
 * el fondo de la pista y el jugador decida con tiempo a que carril irse.
 */
function crearMoneda(): Group {
  const g = new Group();
  const disco = new Mesh(new CylinderGeometry(0.33, 0.33, 0.08, 12), neon(COLORES.moneda));
  disco.rotation.x = Math.PI / 2;
  g.add(disco);
  const borde = new Mesh(new TorusGeometry(0.33, 0.05, 6, 12), neon(COLORES.monedaBorde));
  g.add(borde);
  g.add(halo(COLORES.moneda, 1.5, 0.5));
  g.position.y = 1.1;
  return g;
}

/**
 * Los potenciadores comparten lenguaje: forma geometrica clara, color propio,
 * y un halo del mismo color. El halo no es decoracion — es lo que permite
 * distinguir un iman de un escudo a treinta metros, cuando la forma todavia no
 * se resuelve pero el color ya se ve.
 */
function conHalo(g: Group, color: number, tamano = 1.6): Group {
  g.add(halo(color, tamano, 0.5));
  return g;
}

/** Iman: la U clasica, con las puntas en claro. Atrae las monedas de al lado. */
function crearIman(): Group {
  const g = new Group();
  const arco = new Mesh(
    new TorusGeometry(0.3, 0.12, 6, 10, Math.PI),
    neon(COLORES.iman)
  );
  arco.rotation.z = Math.PI;
  g.add(arco);
  for (const x of [-0.3, 0.3]) {
    const punta = new Mesh(new CylinderGeometry(0.12, 0.12, 0.26, 6), neon(COLORES.imanPunta));
    punta.position.set(x, 0.13, 0);
    g.add(punta);
  }
  g.position.y = 1.2;
  return conHalo(g, COLORES.iman);
}

/** x2: un octaedro. Forma clara y distinta de todo lo demas, con 8 caras. */
function crearX2(): Group {
  const g = new Group();
  const n = new Mesh(new OctahedronGeometry(0.4, 0), neon(COLORES.x2));
  g.add(n);
  g.position.y = 1.2;
  return conHalo(g, COLORES.x2);
}

/** Escudo: un icosaedro, como un cristal. Aguanta un golpe. */
function crearEscudo(): Group {
  const g = new Group();
  const n = new Mesh(new IcosahedronGeometry(0.4, 0), neon(COLORES.escudo));
  g.add(n);
  g.position.y = 1.2;
  return conHalo(g, COLORES.escudo);
}

/** Cohete: te lanza por encima de todo unos segundos. */
function crearCohete(): Group {
  const g = new Group();
  const cuerpo = new Mesh(new CylinderGeometry(0.17, 0.17, 0.6, 8), neon(COLORES.cohete));
  g.add(cuerpo);
  const punta = new Mesh(new ConeGeometry(0.17, 0.3, 8), neon(0xffffff));
  punta.position.y = 0.45;
  g.add(punta);
  const llama = new Mesh(new ConeGeometry(0.14, 0.3, 6), neon(COLORES.coheteLlama));
  llama.position.y = -0.42;
  llama.rotation.x = Math.PI;
  g.add(llama);
  for (const x of [-0.2, 0.2]) {
    const aleta = new Mesh(new BoxGeometry(0.06, 0.2, 0.18), neon(0xffffff));
    aleta.position.set(x, -0.26, 0);
    g.add(aleta);
  }
  g.position.y = 1.2;
  return conHalo(g, COLORES.cohete, 1.8);
}

/** La ballena: el huevo de pascua. Grande, para que se note que es un hallazgo. */
function crearBallena(): Group {
  const g = new Group();
  const cuerpo = new Mesh(new SphereGeometry(0.85, 12, 9), mat(COLORES.ballena));
  cuerpo.scale.set(1, 0.72, 1.7);
  g.add(cuerpo);
  const vientre = new Mesh(new SphereGeometry(0.6, 10, 7), mat(COLORES.ballenaVientre));
  vientre.scale.set(1, 0.5, 1.6);
  vientre.position.y = -0.3;
  g.add(vientre);
  const cola = new Mesh(new ConeGeometry(0.55, 0.7, 4), mat(COLORES.ballena));
  cola.position.z = 1.5;
  cola.rotation.x = -Math.PI / 2;
  cola.scale.set(1, 1, 0.3);
  g.add(cola);
  for (const x of [-0.75, 0.75]) {
    const aleta = new Mesh(new ConeGeometry(0.3, 0.6, 4), mat(COLORES.ballena));
    aleta.position.set(x, -0.1, 0.2);
    aleta.rotation.z = x > 0 ? -1.4 : 1.4;
    aleta.scale.set(1, 1, 0.4);
    g.add(aleta);
  }
  const ojo = new Mesh(new SphereGeometry(0.1, 6, 5), mat(0x101520));
  ojo.position.set(0.55, 0.15, -0.9);
  g.add(ojo);
  g.position.y = 1.8;
  return g;
}

/** Crea todas las plantillas de una vez. Se llama una sola vez, al arrancar. */
export function crearPlantillas(): Plantillas {
  return {
    jugador: crearJugador(),
    barrera: crearBarrera(),
    vela: crearVela(),
    tiburon: crearTiburon(),
    moneda: crearMoneda(),
    iman: crearIman(),
    x2: crearX2(),
    escudo: crearEscudo(),
    cohete: crearCohete(),
    ballena: crearBallena(),
  };
}

// ---------------------------------------------------------------------------
// Decorados y fondo
// ---------------------------------------------------------------------------

/**
 * Textura de ventanas encendidas, dibujada en un canvas.
 *
 * Una rejilla de cuadraditos en la que solo algunos estan encendidos. Es lo que
 * convierte una caja oscura en un edificio: sin ventanas, la ciudad de noche es
 * una mancha negra sobre un fondo negro, que es exactamente como se veia antes
 * del 1-oct-2026.
 *
 * El patron es fijo (no aleatorio por edificio) porque se comparte una sola
 * textura entre los 24 bloques: 24 texturas distintas serian 24 subidas a la
 * tarjeta grafica para algo que nadie va a mirar tan de cerca.
 */
let texturaVentanas: Texture | null = null;

function ventanas(): Texture {
  if (texturaVentanas) return texturaVentanas;

  const lado = 64;
  const lienzo = document.createElement('canvas');
  lienzo.width = lado;
  lienzo.height = lado;
  const ctx = lienzo.getContext('2d')!;
  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, lado, lado);

  // Rejilla de 8x8. Se enciende aproximadamente una de cada tres, con un patron
  // determinista: el mismo edificio se ve igual en cada partida, que es lo que
  // hace que el sitio se reconozca.
  const celda = lado / 8;
  for (let fy = 0; fy < 8; fy++) {
    for (let fx = 0; fx < 8; fx++) {
      const encendida = (fx * 7 + fy * 13 + fx * fy) % 3 === 0;
      if (!encendida) continue;
      // Tres niveles de brillo: una ciudad con todas las ventanas iguales se
      // ve como una rejilla, no como un edificio.
      const brillos = ['#ffffff', '#9fd8ff', '#5aa8e0'];
      ctx.fillStyle = brillos[(fx + fy) % 3] as string;
      ctx.fillRect(fx * celda + celda * 0.22, fy * celda + celda * 0.22, celda * 0.5, celda * 0.42);
    }
  }

  texturaVentanas = new CanvasTexture(lienzo);
  texturaVentanas.wrapS = RepeatWrapping;
  texturaVentanas.wrapT = RepeatWrapping;
  texturaVentanas.needsUpdate = true;
  return texturaVentanas;
}

/**
 * El material de las ventanas encendidas, para el decorado de la ciudad.
 *
 * Va en mezcla aditiva sobre el cubo del edificio: las ventanas SUMAN luz en
 * vez de taparlo, que es como se comporta una ventana encendida de verdad.
 */
export function materialVentanas(): MeshBasicMaterial {
  return new MeshBasicMaterial({
    map: ventanas(),
    transparent: true,
    blending: AdditiveBlending,
    depthWrite: false,
    fog: true,
  });
}

/** El material de los edificios / nubes / asteroides. Color por instancia. */
export function materialDecorado(): MeshLambertMaterial {
  return new MeshLambertMaterial({ color: 0xffffff });
}

/**
 * Campo de estrellas. Points en vez de mallas: 400 estrellas son 400 vertices,
 * no 400 objetos, y se dibujan en una sola pasada.
 */
export function crearEstrellas(cuantas = 420): Points {
  const pos = new Float32Array(cuantas * 3);
  for (let i = 0; i < cuantas; i++) {
    // Repartidas en una cupula delante y arriba del jugador.
    const r = 90 + Math.random() * 110;
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.random() * Math.PI * 0.42;
    pos[i * 3] = Math.sin(phi) * Math.cos(theta) * r;
    pos[i * 3 + 1] = Math.cos(phi) * r * 0.75 + 10;
    pos[i * 3 + 2] = Math.sin(phi) * Math.sin(theta) * r;
  }
  const geo = new BufferGeometry();
  geo.setAttribute('position', new BufferAttribute(pos, 3));
  const material = new PointsMaterial({
    color: 0xffffff,
    size: 1.4,
    // Redondas, por lo mismo que las chispas: una estrella cuadrada se nota.
    map: brillo(),
    sizeAttenuation: true,
    transparent: true,
    blending: AdditiveBlending,
    depthWrite: false,
    opacity: 0.0, // arranca invisible: la subo cuando el bioma las quiere
  });
  const p = new Points(geo, material);
  p.frustumCulled = false;
  return p;
}

/**
 * Particulas para el "juice": el polvo que salta al recoger algo o al morir.
 * Un unico Points reutilizado y movido por CPU. Con 60 particulas no hace falta
 * nada mas complicado, y un sistema de particulas de verdad serian kilobytes.
 */
export class Chispas {
  readonly objeto: Points;
  private readonly posiciones: Float32Array;
  private readonly velocidades: Float32Array;
  private readonly vidas: Float32Array;
  private readonly material: PointsMaterial;
  private readonly cuantas: number;
  private siguiente = 0;

  constructor(cuantas = 60) {
    this.cuantas = cuantas;
    this.posiciones = new Float32Array(cuantas * 3);
    this.velocidades = new Float32Array(cuantas * 3);
    this.vidas = new Float32Array(cuantas);
    // Fuera de la vista hasta que se usen.
    for (let i = 0; i < cuantas; i++) this.posiciones[i * 3 + 1] = -999;

    const geo = new BufferGeometry();
    geo.setAttribute('position', new BufferAttribute(this.posiciones, 3));
    // Con textura redonda y mezcla aditiva. Sin `map`, un Points se dibuja como
    // un CUADRADO solido: se veian cuadraditos naranjas flotando por la pista
    // cada vez que se cogia una moneda. Una chispa cuadrada no parece una
    // chispa, parece un fallo.
    this.material = new PointsMaterial({
      color: 0xffd24a,
      size: 0.3,
      map: brillo(),
      transparent: true,
      opacity: 0.95,
      blending: AdditiveBlending,
      depthWrite: false,
    });
    this.objeto = new Points(geo, this.material);
    this.objeto.frustumCulled = false;
  }

  /** Lanza un punado de chispas desde un punto. */
  emitir(x: number, y: number, z: number, color: number, cuantas = 12): void {
    this.material.color.setHex(color);
    for (let k = 0; k < cuantas; k++) {
      const i = this.siguiente;
      this.siguiente = (this.siguiente + 1) % this.cuantas;
      this.posiciones[i * 3] = x;
      this.posiciones[i * 3 + 1] = y;
      this.posiciones[i * 3 + 2] = z;
      this.velocidades[i * 3] = (Math.random() - 0.5) * 7;
      this.velocidades[i * 3 + 1] = Math.random() * 6 + 1.5;
      this.velocidades[i * 3 + 2] = (Math.random() - 0.5) * 7;
      this.vidas[i] = 0.55;
    }
  }

  actualizar(dt: number): void {
    let alguna = false;
    for (let i = 0; i < this.cuantas; i++) {
      const v = this.vidas[i] as number;
      if (v <= 0) continue;
      alguna = true;
      this.vidas[i] = v - dt;
      const b = i * 3;
      // Se leen a locales en vez de usar += sobre el array: con
      // noUncheckedIndexedAccess, indexar un Float32Array da `number |
      // undefined` y TypeScript no deja sumar sobre eso. Y de paso son tres
      // lecturas menos por particula por cuadro.
      const px = this.posiciones[b] as number;
      const py = this.posiciones[b + 1] as number;
      const pz = this.posiciones[b + 2] as number;
      const vx = this.velocidades[b] as number;
      const vy = this.velocidades[b + 1] as number;
      const vz = this.velocidades[b + 2] as number;
      this.posiciones[b] = px + vx * dt;
      this.posiciones[b + 1] = py + vy * dt;
      this.posiciones[b + 2] = pz + vz * dt;
      this.velocidades[b + 1] = vy - 19 * dt;
      if ((this.vidas[i] as number) <= 0) this.posiciones[b + 1] = -999;
    }
    if (alguna) {
      (this.objeto.geometry.getAttribute('position') as BufferAttribute).needsUpdate = true;
    }
  }
}

/**
 * Destello de pantalla completa para el golpe y el escudo. Un plano delante de
 * la camara es mas barato que un pase de post-proceso, y aqui solo hace falta
 * un flash de color.
 */
export function crearDestello(): Mesh {
  // Un PLANO, no una caja. Una caja de 100x100 tiene seis caras y la camara ve
  // dos de ellas a pantalla completa: se pintaba la pantalla entera dos veces
  // por cuadro para un efecto que casi nunca esta encendido.
  const m = new Mesh(
    new PlaneGeometry(100, 100),
    new MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      // No escribe profundidad ni la comprueba: va delante de todo por
      // `renderOrder`, asi que comparar profundidades es trabajo tirado.
      depthWrite: false,
      depthTest: false,
      fog: false,
    })
  );
  m.position.z = -2;
  m.renderOrder = 999;
  // **Apagado por defecto.** Antes se dibujaba en todos los cuadros con opacidad
  // cero: una pasada transparente a pantalla completa, sesenta veces por
  // segundo, para no pintar nada. En una grafica integrada o en un movil eso es
  // de lo mas caro que hay, porque el coste de una transparencia se paga por
  // pixel y aqui los pixeles son todos.
  m.visible = false;
  return m;
}
